import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../global/supabase';
import { SEAL_CONFIG, getSealFields, formatMedicationName, getLocationMedications } from '../config';
import HamburgerMenu from '../global/menu';
import Toast from '../global/Toast';
import { isSelfWitnessed } from '../global/selfWitness';
import './locations.css';

/**
 * GENERIC LOCATION INVENTORY COMPONENT
 * =====================================
 * This single component handles ANY location type.
 * It reads medications, seals, and settings from config.js
 * Supports locations with multiple medication sections (e.g., ALS + Medic 415)
 */

function LocationInventory({ locationConfig, userInfo, isAdmin, onLogout, onNavigate }) {
  // Get medications for this specific location (may have sections)
  const locationMeds = getLocationMedications(locationConfig.id);
  const medicationSections = locationMeds.sections;

  // Flatten all medication IDs for form state
  const allMedicationIds = medicationSections.flatMap(section =>
    section.medications.map(m => m.id)
  );

  const sealFields = getSealFields(locationConfig.type);
  const hasSealTracking = SEAL_CONFIG[locationConfig.type]?.enabled || false;

  // State management
  const [inventory, setInventory] = useState({});
  const [lastUpdated, setLastUpdated] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showSignatureCanvas, setShowSignatureCanvas] = useState(false);
  const [currentSignatureType, setCurrentSignatureType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inventoryHistory, setInventoryHistory] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Addendum (admin notes) state
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [recordAddendums, setRecordAddendums] = useState([]);
  const [isLoadingAddendums, setIsLoadingAddendums] = useState(false);
  const [newAddendumText, setNewAddendumText] = useState('');
  const [isSavingAddendum, setIsSavingAddendum] = useState(false);
  const [addendumCounts, setAddendumCounts] = useState({});

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  // Form state - initialize with empty values for all medications and seals
  const [formData, setFormData] = useState(() => {
    const initial = {
      reasonForChange: '',
      runNumber: '',
      additionalNotes: '',
      inventoryPicture: null,
      secondaryLicense: '',
      secondaryPin: '',
      secondaryUserInfo: null,
      primarySignature: null,
      secondarySignature: null
    };
    // Add medication fields (all IDs from all sections)
    allMedicationIds.forEach(med => { initial[med] = ''; });
    // Add seal fields
    sealFields.forEach(seal => { initial[seal.id] = ''; });
    return initial;
  });

  // Canvas ref
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Fetch latest inventory
  const fetchLiveInventory = async () => {
    try {
      const { data, error } = await supabase
        .from(locationConfig.tableName)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;

      if (data && data.length > 0) {
        const record = data[0];
        const inventoryData = {};

        // Extract medication values
        allMedicationIds.forEach(med => {
          inventoryData[med] = record[med] ?? 'N/A';
        });

        // Extract seal values
        sealFields.forEach(seal => {
          inventoryData[seal.id] = record[seal.id] ?? 'N/A';
        });

        setInventory(inventoryData);

        const date = new Date(record.created_at);
        setLastUpdated(date.toLocaleString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }) + ' EST');
      } else {
        const inventoryData = {};
        allMedicationIds.forEach(med => { inventoryData[med] = 'N/A'; });
        sealFields.forEach(seal => { inventoryData[seal.id] = 'N/A'; });
        setInventory(inventoryData);
        setLastUpdated('No previous updates.');
      }
    } catch (error) {
      console.error('Error fetching live inventory:', error);
    }
  };

  // Fetch history
  const fetchInventoryHistory = async () => {
    try {
      const { data, error } = await supabase
        .from(locationConfig.tableName)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      const records = data || [];
      setInventoryHistory(records);

      // Fetch addendum counts for this location's records so
      // we can show a "Notes (n)" indicator in the history list.
      if (records.length > 0) {
        const recordIds = records.map(r => r.id);
        const { data: addendumRows, error: addendumError } = await supabase
          .from('inventory_addendums')
          .select('record_id')
          .eq('table_name', locationConfig.tableName)
          .in('record_id', recordIds);

        if (addendumError) {
          console.error('Error fetching addendum counts:', addendumError);
          setAddendumCounts({});
        } else {
          const counts = {};
          (addendumRows || []).forEach(row => {
            counts[row.record_id] = (counts[row.record_id] || 0) + 1;
          });
          setAddendumCounts(counts);
        }
      } else {
        setAddendumCounts({});
      }
    } catch (error) {
      console.error('Error fetching inventory history:', error);
    }
  };

  // Fetch all addendums attached to a specific inventory record
  const fetchAddendumsForRecord = async (recordId) => {
    setIsLoadingAddendums(true);
    try {
      const { data, error } = await supabase
        .from('inventory_addendums')
        .select('*')
        .eq('table_name', locationConfig.tableName)
        .eq('record_id', recordId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setRecordAddendums(data || []);
    } catch (error) {
      console.error('Error fetching addendums:', error);
      setRecordAddendums([]);
    } finally {
      setIsLoadingAddendums(false);
    }
  };

  // Open the record detail/addendum view
  const openRecordDetails = (record) => {
    setSelectedRecord(record);
    setNewAddendumText('');
    fetchAddendumsForRecord(record.id);
  };

  const closeRecordDetails = () => {
    setSelectedRecord(null);
    setRecordAddendums([]);
    setNewAddendumText('');
  };

  // Admin-only: append a new addendum to a record
  const handleAddAddendum = async () => {
    if (!isAdmin) return;
    if (isSavingAddendum) return;

    const trimmed = newAddendumText.trim();
    if (!trimmed) {
      showToast('Note cannot be empty.', 'warning');
      return;
    }

    if (!selectedRecord) return;

    setIsSavingAddendum(true);
    try {
      const { error } = await supabase
        .from('inventory_addendums')
        .insert([{
          table_name: locationConfig.tableName,
          record_id: selectedRecord.id,
          admin_user_id: userInfo.id,
          admin_user_name: `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim(),
          admin_user_email: userInfo.email || '',
          note_text: trimmed
        }]);

      if (error) throw error;

      setNewAddendumText('');
      await fetchAddendumsForRecord(selectedRecord.id);
      setAddendumCounts(prev => ({
        ...prev,
        [selectedRecord.id]: (prev[selectedRecord.id] || 0) + 1
      }));
      showToast('Note added successfully.', 'success');
    } catch (error) {
      console.error('Error adding addendum:', error);
      showToast('Error adding note. Please try again.', 'error');
    } finally {
      setIsSavingAddendum(false);
    }
  };

  // Signature canvas methods
  const setupCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    canvas.width = Math.min(window.innerWidth * 0.8, 500);
    canvas.height = window.innerHeight * 0.4;

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.lineWidth = 2;
    context.strokeStyle = '#000000';
    context.lineJoin = 'round';
    context.lineCap = 'round';
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();

    let x, y;
    if (e.touches) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    context.beginPath();
    context.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();

    let x, y;
    if (e.touches) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    context.lineTo(x, y);
    context.stroke();
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    context.clearRect(0, 0, canvas.width, canvas.height);
  };

  const confirmSignature = async () => {
    const canvas = canvasRef.current;
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;

    const tempContext = tempCanvas.getContext('2d');
    tempContext.fillStyle = '#FFFFFF';
    tempContext.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
    tempContext.drawImage(canvas, 0, 0);

    const signatureImage = tempCanvas.toDataURL('image/png');

    try {
      const response = await fetch(signatureImage);
      const blob = await response.blob();

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `${locationConfig.id}-${currentSignatureType}-${timestamp}-${crypto.randomUUID()}.png`;

      const { error: uploadError } = await supabase.storage
        .from('signatures')
        .upload(fileName, blob, { contentType: 'image/png', upsert: false });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        showToast('Error uploading signature. Please try again.', 'error');
        return;
      }

      // The bucket is private: store the object path (read later with createSignedUrl).
      if (currentSignatureType === 'primary') {
        setFormData(prev => ({ ...prev, primarySignature: fileName }));
      } else {
        setFormData(prev => ({ ...prev, secondarySignature: fileName }));
      }

      setShowSignatureCanvas(false);
      clearCanvas();
      setCurrentSignatureType(null);
    } catch (error) {
      console.error('Error processing signature:', error);
      showToast('Error processing signature. Please try again.', 'error');
    }
  };

  // Secondary user validation
  const validateSecondaryUser = async () => {
    try {
      const { data, error } = await supabase.rpc('validate_secondary_user', {
        license_number: formData.secondaryLicense,
        user_pin: formData.secondaryPin
      });

      // The server never says whether the license or the PIN was wrong, and
      // locks a witness after repeated failures.
      const userData = data && data[0];
      if (error || !userData || !userData.is_valid) {
        showToast(error?.message === 'You cannot witness your own entry'
          ? 'You cannot witness your own entry.'
          : 'Invalid license number or PIN, or too many attempts. Try again later.', 'error');
        return;
      }

      setFormData(prev => ({
        ...prev,
        secondaryUserInfo: {
          firstName: userData.first_name,
          lastName: userData.last_name,
          // the license just validated, stored as secondary_user_license
          stateLicenseNumber: formData.secondaryLicense.trim()
        }
      }));
    } catch (error) {
      console.error('Error validating secondary user:', error);
      showToast('Error validating credentials.', 'error');
    }
  };

  // Image capture
  const handleImageCapture = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'camera';

    input.onchange = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      try {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const fileName = `${locationConfig.id}-inventory-${timestamp}-${crypto.randomUUID()}.jpg`;

        const { error: uploadError } = await supabase.storage
          .from('inventory-photos')
          .upload(fileName, file, { contentType: file.type, upsert: false });

        if (uploadError) {
          showToast('Error uploading image.', 'error');
          return;
        }

        setFormData(prev => ({ ...prev, inventoryPicture: fileName }));
      } catch (error) {
        console.error('Error processing image:', error);
      }
    };

    input.click();
  };

  // Form submission
  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!formData.primarySignature) {
      showToast('Primary signature required.', 'warning');
      return;
    }

    if (!formData.reasonForChange) {
      showToast('Reason for Change required.', 'warning');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        user_id: userInfo.id,
        user_name: `${userInfo.firstName} ${userInfo.lastName}`,
        user_email: userInfo.email,
        user_license: userInfo.stateLicenseNumber || '',
        location: locationConfig.name,
        reason_for_change: formData.reasonForChange,
        run_number: formData.runNumber || '',
        additional_notes: formData.additionalNotes || '',
        primary_signature_url: formData.primarySignature,
        secondary_signature_url: formData.secondarySignature || '',
        secondary_user_name: formData.secondaryUserInfo
          ? `${formData.secondaryUserInfo.firstName} ${formData.secondaryUserInfo.lastName}`
          : '',
        secondary_user_license: formData.secondaryUserInfo?.stateLicenseNumber || '',
        inventory_picture_url: formData.inventoryPicture || ''
      };

      // Add medication values
      allMedicationIds.forEach(med => {
        submitData[med] = formData[med] || 0;
      });

      // Add seal values
      sealFields.forEach(seal => {
        submitData[seal.id] = formData[seal.id] || '';
      });

      const { error } = await supabase.from(locationConfig.tableName).insert([submitData]);

      if (error) throw error;

      showToast('Inventory logged successfully!', 'success');
      setShowModal(false);

      // Reset form
      const resetData = {
        reasonForChange: '',
        runNumber: '',
        additionalNotes: '',
        inventoryPicture: null,
        secondaryLicense: '',
        secondaryPin: '',
        secondaryUserInfo: null,
        primarySignature: null,
        secondarySignature: null
      };
      allMedicationIds.forEach(med => { resetData[med] = ''; });
      sealFields.forEach(seal => { resetData[seal.id] = ''; });
      setFormData(resetData);

      fetchLiveInventory();
    } catch (error) {
      console.error('Error submitting form:', error);
      showToast('Error submitting inventory. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Lifecycle
  useEffect(() => {
    fetchLiveInventory();
  }, [locationConfig.tableName]);

  useEffect(() => {
    if (showSignatureCanvas) setupCanvas();
  }, [showSignatureCanvas]);

  // Render
  return (
    <div className="container">
      <HamburgerMenu isAdmin={isAdmin} onLogout={onLogout} onNavigate={onNavigate} />

      <div className="location-container">
        {/* Header */}
        <div className="row">
          <div className="col text-center">
            <h1 className="location-title">{locationConfig.name}</h1>
            <p className="location-last-updated">Last Updated: <span>{lastUpdated}</span></p>
          </div>
        </div>

        {/* Current Inventory Display */}
        <div className="location-inventory-holder">
          {/* Medications - with sections if applicable */}
          {medicationSections.map((section, sectionIndex) => (
            <React.Fragment key={sectionIndex}>
              {section.title && (
                <h3 className="location-section-title">{section.title}</h3>
              )}
              {section.medications.map(med => (
                <div key={med.id} className="location-inventory-item">
                  <span className="location-medication-name">{med.displayName}</span>
                  <span className="location-inventory-count">{inventory[med.id] ?? 'N/A'}</span>
                </div>
              ))}
            </React.Fragment>
          ))}

          {/* Seals/Tags (if applicable) */}
          {hasSealTracking && sealFields.map(seal => (
            <div key={seal.id} className="location-inventory-item">
              <span className="location-medication-name">
                {seal.displayName}
                {seal.hint && <small style={{ fontSize: '0.8em' }}> {seal.hint}</small>}
              </span>
              <span className="location-inventory-count">{inventory[seal.id] ?? 'N/A'}</span>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="row">
          <div className="col text-center">
            <button
              className="location-change-inventory-btn"
              onClick={() => setShowModal(true)}
              disabled={!userInfo?.isEditor}
            >
              Change Inventory
            </button>
          </div>
        </div>

        <div className="row">
          <div className="col text-center">
            <button
              className="location-inventory-logs-btn"
              onClick={() => { fetchInventoryHistory(); setShowLogsModal(true); }}
            >
              Inventory Logs
            </button>
          </div>
        </div>

        {/* Inventory Modal */}
        {showModal && (
          <div className="location-modal-overlay">
            <div className="location-modal-content">
              <button className="location-modal-close" onClick={() => setShowModal(false)}>x</button>

              <div className="location-modal-header">
                {userInfo?.firstName} {userInfo?.lastName}
              </div>

              <div className="location-modal-instructions">
                Complete this form to log inventory changes. All fields marked with * are required.
              </div>

              {/* Medication Quantities - with sections if applicable */}
              {medicationSections.map((section, sectionIndex) => (
                <div key={sectionIndex} className="location-form-section">
                  <h6>{section.title || 'Medication Inventory'}</h6>
                  {section.medications.map(med => (
                    <div key={med.id} className="location-form-group">
                      <label className="location-form-label">{med.displayName}</label>
                      <input
                        type="number"
                        className="location-form-input"
                        value={formData[med.id] || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, [med.id]: e.target.value }))}
                        placeholder="Enter quantity"
                      />
                    </div>
                  ))}
                </div>
              ))}

              {/* Seal Numbers (if applicable) */}
              {hasSealTracking && (
                <div className="location-form-section">
                  <h6>Seal Numbers</h6>
                  {sealFields.map(seal => (
                    <div key={seal.id} className="location-form-group">
                      <label className="location-form-label">
                        {seal.displayName}
                        {seal.hint && <small> {seal.hint}</small>}
                      </label>
                      <input
                        type="text"
                        className="location-form-input"
                        value={formData[seal.id] || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, [seal.id]: e.target.value }))}
                        placeholder={`Enter ${seal.displayName}`}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Reason for Change */}
              <div className="location-form-section">
                <div className="location-form-group">
                  <label className="location-form-label">Reason for Change *</label>
                  <select
                    className="location-form-select"
                    value={formData.reasonForChange}
                    onChange={(e) => setFormData(prev => ({ ...prev, reasonForChange: e.target.value }))}
                    required
                  >
                    <option value="">Select a reason</option>
                    <option value="Daily Inventory Check">Daily Inventory Check</option>
                    <option value="Expiration/Restock">Expiration/Restock</option>
                    <option value="Patient Use/Restock">Patient Use/Restock</option>
                  </select>
                </div>

                {formData.reasonForChange === 'Patient Use/Restock' && (
                  <div className="location-form-group">
                    <label className="location-form-label">Run #</label>
                    <input
                      type="text"
                      className="location-form-input"
                      value={formData.runNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, runNumber: e.target.value }))}
                      placeholder="Enter Run #"
                    />
                  </div>
                )}
              </div>

              {/* Image Upload */}
              <div className="location-form-section">
                <h6>Add Image</h6>
                <button className="location-upload-btn" type="button" onClick={handleImageCapture}>
                  Capture Image
                </button>
                {formData.inventoryPicture && (
                  <img src={formData.inventoryPicture} alt="Inventory Preview" className="location-image-preview" />
                )}
              </div>

              {/* Primary Signature */}
              <div className="location-form-section">
                <h6>Primary Signature *</h6>
                <button
                  className="location-btn location-btn-primary"
                  onClick={() => { setCurrentSignatureType('primary'); setShowSignatureCanvas(true); }}
                >
                  Sign
                </button>
                <div className="location-signature-placeholder">
                  {formData.primarySignature ? (
                    <img src={formData.primarySignature} alt="Primary Signature" className="location-signature-image" />
                  ) : 'Signature will appear here'}
                </div>
              </div>

              {/* Secondary Signature */}
              <div className="location-form-section">
                <h6>Secondary Signature</h6>
                <div className="location-form-group">
                  <label className="location-form-label">Employee ID Number</label>
                  <input
                    type="text"
                    className="location-form-input"
                    value={formData.secondaryLicense}
                    onChange={(e) => setFormData(prev => ({ ...prev, secondaryLicense: e.target.value }))}
                    placeholder="Enter employee ID"
                  />
                </div>
                <div className="location-form-group">
                  <label className="location-form-label">PIN</label>
                  <input
                    type="password"
                    className="location-form-input"
                    value={formData.secondaryPin}
                    onChange={(e) => setFormData(prev => ({ ...prev, secondaryPin: e.target.value }))}
                    placeholder="Enter PIN"
                  />
                </div>
                <button className="location-btn location-btn-primary" onClick={validateSecondaryUser}>
                  Confirm
                </button>

                {formData.secondaryUserInfo && (
                  <div className="location-secondary-confirmed">
                    <div className="location-secondary-badge">
                      <span className="location-secondary-checkmark">&#10003;</span>
                      <span className="location-secondary-name">
                        {formData.secondaryUserInfo.firstName} {formData.secondaryUserInfo.lastName}
                      </span>
                    </div>
                    <button
                      className="location-btn location-btn-primary"
                      onClick={() => { setCurrentSignatureType('secondary'); setShowSignatureCanvas(true); }}
                    >
                      Sign
                    </button>
                    <div className="location-signature-placeholder">
                      {formData.secondarySignature ? (
                        <img src={formData.secondarySignature} alt="Secondary Signature" className="location-signature-image" />
                      ) : 'Signature will appear here'}
                    </div>
                  </div>
                )}
              </div>

              {/* Additional Notes */}
              <div className="location-form-section">
                <h6>Additional Notes</h6>
                <textarea
                  className="location-form-textarea"
                  value={formData.additionalNotes}
                  onChange={(e) => setFormData(prev => ({ ...prev, additionalNotes: e.target.value }))}
                  placeholder="Enter any additional notes"
                />
              </div>

              {/* Submit */}
              <div className="location-form-section">
                <button
                  className="location-btn location-btn-primary location-btn-full"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Complete Form'}
                </button>
                <button
                  className="location-btn location-btn-secondary location-btn-full"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Signature Canvas */}
        {showSignatureCanvas && (
          <div className="location-signature-overlay">
            <div className="location-signature-container">
              <h5>Draw Your Signature</h5>
              <canvas
                ref={canvasRef}
                className="location-signature-canvas"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={(e) => { e.preventDefault(); startDrawing(e); }}
                onTouchMove={(e) => { e.preventDefault(); draw(e); }}
                onTouchEnd={(e) => { e.preventDefault(); stopDrawing(); }}
              />
              <div className="location-signature-controls">
                <button className="location-signature-btn clear" onClick={clearCanvas}>Clear</button>
                <button className="location-signature-btn confirm" onClick={confirmSignature}>Confirm</button>
              </div>
            </div>
          </div>
        )}

        {/* History Modal */}
        {showLogsModal && (
          <div className="location-modal-overlay">
            <div className="location-modal-content location-modal-content-wide">
              <button className="location-modal-close" onClick={() => setShowLogsModal(false)}>x</button>
              <div className="location-modal-header">Inventory History - {locationConfig.name}</div>

              <div className="location-history-scroll">
                <table className="location-history-table location-history-table-sticky">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Name</th>
                      <th>Reason</th>
                      {allMedicationIds.map(med => (
                        <th key={med}>{formatMedicationName(med)}</th>
                      ))}
                      {hasSealTracking && sealFields.map(seal => (
                        <th key={seal.id}>{seal.displayName.split(' ')[0]}</th>
                      ))}
                      <th>Notes</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventoryHistory.map(record => {
                      const noteCount = addendumCounts[record.id] || 0;
                      return (
                        <tr key={record.id}>
                          <td>{record.created_at ? new Date(record.created_at).toLocaleDateString() : ''}</td>
                          <td>{record.created_at ? new Date(record.created_at).toLocaleTimeString() : ''}</td>
                          <td>{record.user_name || ''}</td>
                          <td>{record.reason_for_change}</td>
                          {allMedicationIds.map(med => (
                            <td key={med}>{record[med]}</td>
                          ))}
                          {hasSealTracking && sealFields.map(seal => (
                            <td key={seal.id}>{record[seal.id]}</td>
                          ))}
                          <td>
                            {noteCount > 0 ? (
                              <span className="location-addendum-badge">{noteCount}</span>
                            ) : (
                              <span style={{ color: 'var(--text-muted)' }}>-</span>
                            )}
                          </td>
                          <td>
                            <button
                              className="location-btn location-btn-primary"
                              style={{ padding: '6px 12px', fontSize: '0.85em' }}
                              onClick={() => openRecordDetails(record)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <button className="location-btn location-btn-secondary" onClick={() => setShowLogsModal(false)}>
                Close
              </button>
            </div>
          </div>
        )}

        {/* Record Detail / Addendum Modal */}
        {selectedRecord && (
          <div className="location-modal-overlay" style={{ zIndex: 1100 }}>
            <div className="location-modal-content">
              <button className="location-modal-close" onClick={closeRecordDetails}>x</button>
              <div className="location-modal-header">Inventory Record Details</div>

              <div className="location-modal-instructions">
                This record is read-only. Medications, quantities, and seal
                numbers cannot be changed. Admins can attach notes below to
                document context or explanations for this entry.
              </div>

              {/* Record metadata */}
              <div className="location-form-section">
                <h6>Record Info</h6>
                <div className="location-detail-row">
                  <span className="location-detail-label">Date / Time</span>
                  <span className="location-detail-value">
                    {selectedRecord.created_at
                      ? new Date(selectedRecord.created_at).toLocaleString()
                      : '-'}
                  </span>
                </div>
                <div className="location-detail-row">
                  <span className="location-detail-label">Submitted By</span>
                  <span className="location-detail-value">{selectedRecord.user_name || '-'}</span>
                </div>
                <div className="location-detail-row">
                  <span className="location-detail-label">License</span>
                  <span className="location-detail-value">{selectedRecord.user_license || '-'}</span>
                </div>
                <div className="location-detail-row">
                  <span className="location-detail-label">Reason</span>
                  <span className="location-detail-value">{selectedRecord.reason_for_change || '-'}</span>
                </div>
                {selectedRecord.run_number && (
                  <div className="location-detail-row">
                    <span className="location-detail-label">Run #</span>
                    <span className="location-detail-value">{selectedRecord.run_number}</span>
                  </div>
                )}
                {selectedRecord.secondary_user_name && (
                  <div className="location-detail-row">
                    <span className="location-detail-label">Secondary</span>
                    <span className="location-detail-value">
                      {selectedRecord.secondary_user_name}
                      {isSelfWitnessed(selectedRecord, { [userInfo?.id]: userInfo?.stateLicenseNumber }) && (
                        <span style={{
                          marginLeft: '8px',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontSize: '11px',
                          fontWeight: '600',
                          background: '#fff3e0',
                          color: '#b26a00'
                        }}>
                          Self-witnessed
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>

              {/* Medications (read-only) */}
              <div className="location-form-section">
                <h6>Medications</h6>
                {medicationSections.map((section, sectionIndex) => (
                  <React.Fragment key={sectionIndex}>
                    {section.title && (
                      <div className="location-detail-subheading">{section.title}</div>
                    )}
                    {section.medications.map(med => (
                      <div key={med.id} className="location-detail-row">
                        <span className="location-detail-label">{med.displayName}</span>
                        <span className="location-detail-value">
                          {selectedRecord[med.id] ?? '-'}
                        </span>
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>

              {/* Seals (read-only) */}
              {hasSealTracking && (
                <div className="location-form-section">
                  <h6>Seal Numbers</h6>
                  {sealFields.map(seal => (
                    <div key={seal.id} className="location-detail-row">
                      <span className="location-detail-label">{seal.displayName}</span>
                      <span className="location-detail-value">
                        {selectedRecord[seal.id] || '-'}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Original additional notes (if present) */}
              {selectedRecord.additional_notes && (
                <div className="location-form-section">
                  <h6>Original Notes</h6>
                  <div className="location-detail-text">
                    {selectedRecord.additional_notes}
                  </div>
                </div>
              )}

              {/* Addendums (admin notes) */}
              <div className="location-form-section">
                <h6>Admin Notes / Addendums</h6>

                {isLoadingAddendums ? (
                  <div style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                    Loading notes...
                  </div>
                ) : recordAddendums.length === 0 ? (
                  <div style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                    No notes have been added to this record.
                  </div>
                ) : (
                  <div className="location-addendum-list">
                    {recordAddendums.map(note => (
                      <div key={note.id} className="location-addendum-item">
                        <div className="location-addendum-meta">
                          <strong>{note.admin_user_name || 'Admin'}</strong>
                          <span>
                            {note.created_at
                              ? new Date(note.created_at).toLocaleString()
                              : ''}
                          </span>
                        </div>
                        <div className="location-addendum-text">{note.note_text}</div>
                      </div>
                    ))}
                  </div>
                )}

                {isAdmin && (
                  <div style={{ marginTop: '16px' }}>
                    <label className="location-form-label">Add a Note</label>
                    <textarea
                      className="location-form-textarea"
                      value={newAddendumText}
                      onChange={(e) => setNewAddendumText(e.target.value)}
                      placeholder="Enter an explanation or addendum for this record..."
                      disabled={isSavingAddendum}
                    />
                    <button
                      className="location-btn location-btn-primary location-btn-full"
                      style={{ marginTop: '10px' }}
                      onClick={handleAddAddendum}
                      disabled={isSavingAddendum || !newAddendumText.trim()}
                    >
                      {isSavingAddendum ? 'Saving...' : 'Add Note'}
                    </button>
                  </div>
                )}
              </div>

              <button
                className="location-btn location-btn-secondary location-btn-full"
                onClick={closeRecordDetails}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Toast Notification */}
        {toast.show && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast({ ...toast, show: false })}
          />
        )}
      </div>
    </div>
  );
}

export default LocationInventory;
